import React from "react";
import { SearchListingResult } from "../../Utilities/BusinessData";
import { utils, addtoCartEnum } from "../../../utils/Utils";
import RenderStarRating from "../../Icons/StarRating";
import { decodeAmp } from "../../Utilities/StringUtils";

type DirectoryProCardVariant = "loc-gallery" | "directory";

interface DirectoryProCardProps {
    item: SearchListingResult;
    variant: DirectoryProCardVariant;
    hasDateTimeSelected: boolean;
    isStenciled?: boolean;
    proNameSrc?: string;
    proPhotoSrc?: string;
    enterpriseType?: string;
    firstResultList?: SearchListingResult[];
    // link target/href used by both variants
    href?: string;
    target?: "_self" | "_blank";

    // directory specific
    index?: number;
    isActive?: boolean;
    isLocationHighlighted?: boolean;
    onClick?: () => void;
    onBookClick?: (item: SearchListingResult) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

const useLocatoinNickname = true; // todo pass as prop for sola specifcally.


export default function DirectoryProCard({ firstResultList,item, variant, hasDateTimeSelected, isStenciled, proNameSrc, proPhotoSrc, enterpriseType, href, target, index, isActive, isLocationHighlighted, onClick, onBookClick, onMouseEnter, onMouseLeave }: DirectoryProCardProps) {
    const isVagaroPro = item.isdormant === false;
    const displayName = (proNameSrc === "owner" ? (item.ownerName || item.businessName) : decodeAmp(item.businessName));
    const displayPhoto = proPhotoSrc === "owner" ? (item.ownerPhoto || item.businessImagePath) : item.businessImagePath;
    const suiteText = decodeAmp(formatSuiteText(item, enterpriseType)); 
    const initials = (displayName || "?").charAt(0).toUpperCase();
    const resolvedFacilityValue = item.facilityValue
        || (firstResultList?.find(r => r.vagaroURL === item.vagaroURL)?.facilityValue ?? "");

    const facilityChips = resolvedFacilityValue
        ? resolvedFacilityValue.split(",").map(v => v.trim()).filter(Boolean).slice(0, 2)
        : [];

    const handleBook = () => {
        if (isStenciled) return;
        if (variant === "directory" && onBookClick) {
            onBookClick(item);
            return;
        }

        const bookingUrl = buildBookingUrl(item);
        const wbc = (window as any).wbc;
        if (wbc?.openVagaroPage) {
            wbc.openVagaroPage("divVagaroIframe", bookingUrl, undefined, true);
        }
    };

    const canBook = !isStenciled && isVagaroPro && hasDateTimeSelected && !!item.strFreeTime;

    if (variant === "loc-gallery") {
        // canBook → whole card opens booking iframe; otherwise → navigate via href
        const handleLocGalleryClick = (e: React.MouseEvent) => {
            if (isStenciled) return;
            if (canBook) {
                e.preventDefault();
                handleBook();
            }
            // else: let the <a> navigate normally
        };

        return (
            <a
                className={"dir-card-wrapper" + (isStenciled ? " stenciled" : "")}
                href={href || "#"}
                target={target || "_blank"}
                rel={target === "_blank" ? "noopener noreferrer" : undefined}
                onClick={handleLocGalleryClick}
            >
                <div className="dir-card">
                    <div className="dir-content">
                        {displayPhoto && !isStenciled ? (
                            <div className="dir-image stencil-item staff-image-border" style={{ backgroundImage: `url(${displayPhoto})` }} />
                        ) : (
                            <div className="dir-initials stencil-item staff-image-border staff-initials">
                                <div className="dir-initials-text">{isStenciled ? "\u00A0" : initials}</div>
                            </div>
                        )}
                        <div className="dir-profile-text">
                            <h2 className="dir-pro-name stencil-item">{isStenciled ? "\u00A0" : displayName}</h2>
                            <div className="dir-biz-name stencil-item">{isStenciled ? "\u00A0" : decodeAmp(item.businessName)}</div>
                            {!isStenciled && suiteText && (
                                <div className="dir-suite-label">{suiteText}</div>
                            )}
                            {isStenciled ? "\u00A0" : <div className="dir-service-label stencil-item">{item.serviceTitle}</div>}
                        </div>
                    </div>
                    {isStenciled ? (
                        <div className="dir-chip-row">
                            <div className="dir-chip stencil-item">&nbsp;</div>
                            <div className="dir-chip stencil-item">&nbsp;</div>
                        </div>
                    ) : canBook ? (
                        <div className="dir-book-btn">
                            <span className="dir-book-btn-label">Book | {formatBookingLabel(item.appointmentDate, item.strFreeTime)}</span>
                            {item.isShowPriceOnline && item.price > 0 && (
                                <span className="dir-pro-card-price">${item.price.toFixed(2)}</span>
                            )}
                        </div>
                    ) : facilityChips.length > 0 ? (
                        <div className="dir-chip-row">
                            {facilityChips.map((chip, i) => (
                                <div key={i} className="dir-chip">{chip}</div>
                            ))}
                        </div>
                    ) : (
                        <div className="dir-availability-slot" />
                    )}
                </div>
            </a>
        );
    }

    // variant === "directory"
    // canBook → whole card opens booking iframe; otherwise → navigate to pro page
    const handleDirectoryClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (isStenciled) return;
        if (canBook && onBookClick) {
            onBookClick(item);
        } else if (onClick) {
            onClick();
        }
    };

    let loc = (useLocatoinNickname)? item.locationName||item.city : item.city;

    return (
        <a
            className={"dir-pro-card" + (isStenciled ? " stenciled" : "") + (isActive ? " active" : "") + (isLocationHighlighted ? " dir-pro-card--location-highlight" : "")}
            href={href || "#"}
            target={target || "_blank"}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
            data-card-index={index}
            onClick={handleDirectoryClick}
            onMouseEnter={isStenciled ? undefined : onMouseEnter}
            onMouseLeave={isStenciled ? undefined : onMouseLeave}
        >
            <div className={"dir-pro-card-photo stencil-item" + (!displayPhoto || isStenciled ? " dir-pro-card-photo-initials" : "")}
                style={displayPhoto && !isStenciled ? { backgroundImage: `url(${displayPhoto})` } : undefined}>
                {(!displayPhoto || isStenciled) && <span>{isStenciled ? "\u00A0" : initials}</span>}
            </div>
            <div className="dir-pro-card-info">
                <div className="dir-pro-card-name stencil-item">{isStenciled ? "\u00A0" : displayName}</div>
                {(isStenciled || item.averageRank > 0) && (
                    <div className="dir-pro-card-rating stencil-item">
                        {isStenciled ? "\u00A0" : (
                            <>
                                <span className="dir-pro-card-stars"><RenderStarRating rating={Math.round(item.averageRank)} starColor="var(--theme-color-3)" isOutlined={false} /></span>
                                <span className="dir-pro-card-reviews">{item.averageRank.toFixed(1)} ({item.reviewTotal} Reviews)</span>
                            </>
                        )}
                    </div>
                )}
                {(isStenciled || loc) && <div className="dir-pro-card-location stencil-item">{isStenciled ? "\u00A0" : loc}</div>}
                {(isStenciled || item.serviceTitle) && <div className="dir-pro-card-service stencil-item">{isStenciled ? "\u00A0" : item.serviceTitle}</div>}
                {!isStenciled && !isVagaroPro && facilityChips.length > 0 && (
                    <div className="dir-chip-row">
                        {facilityChips.map((chip, i) => (
                            <span key={i} className="dir-chip">{chip}</span>
                        ))}
                    </div>
                )}
                <div className="dir-pro-card-bottom">
                    {!isStenciled && canBook && (
                        <div className="dir-book-btn">
                            <span className="dir-book-btn-label">Book | {formatBookingLabel(item.appointmentDate, item.strFreeTime)}</span>
                            {item.isShowPriceOnline && item.price > 0 && (
                                <span className="dir-pro-card-price">${item.price.toFixed(2)}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </a>
    );
}

export function buildBookingUrl(item: SearchListingResult): string {
    const jsonData = JSON.stringify({
        encserviceid: item.encServiceId,
        providerID: item.serviceProviderID,
        styleID: 0,
        styleName: "",
        bookingDateTime: item.strFreeTime || "",
        PromotionID: "",
        EventType: item.eventType.toString(),
        isdeeplinkentry: null,
        PromotionProviderID: "",
        AddonData: false,
    }).replace(/\\\"/g, '"').replace(/"/g, '\\"');

    const url = utils.VURL(null, item.vagaroURL) + addtoCartEnum.ServiceBook + "&Data=" + jsonData;
    return url;
}

function formatSuiteText(item: SearchListingResult, enterpriseType?: string): string {
    if (!enterpriseType) return "";
    switch (enterpriseType) {
        case "sola":
            if (item.studioNumber && item.studioNumber.length > 0) {
                return "Studio " + item.studioNumber + " at Sola Salons " + (item.locationName || "");
            }
            return "Studio at Sola Salons " + (item.locationName || "");
        default:
            return "";
    }
}

function formatBookingLabel(appointmentDate: string, strFreeTime: string): string {
    const apptDate = new Date(appointmentDate);
    if (isNaN(apptDate.getTime())) return strFreeTime;

    const timeMatch = strFreeTime.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*$/i);
    const timePart = timeMatch ? timeMatch[1].replace(/^0/, "") : strFreeTime;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const apptDay = new Date(apptDate);
    apptDay.setHours(0, 0, 0, 0);

    let dateLabel: string;
    if (apptDay.getTime() === today.getTime()) {
        dateLabel = "Today";
    } else if (apptDay.getTime() === tomorrow.getTime()) {
        dateLabel = "Tomorrow";
    } else {
        dateLabel = apptDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }

    return `${dateLabel} - ${timePart}`;
}
